import React, { useEffect, useState } from 'react';
import { pmAdminApi } from '../../lib/pmApi';
import { CheckCircle2, Clock, XCircle, ShieldCheck } from 'lucide-react';

const STATUS_STYLE = {
  Pending:  { icon: Clock,        cls: 'bg-[#D4AF37]/15 text-[#D4AF37]' },
  Approved: { icon: CheckCircle2, cls: 'bg-[#10B981]/15 text-[#10B981]' },
  Rejected: { icon: XCircle,      cls: 'bg-[#EF4444]/15 text-[#EF4444]' },
};

const PmPropertyRequests = () => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('Pending');
  const [open, setOpen] = useState(null);
  const [decision, setDecision] = useState({ reason: '', limit: '500' });

  const load = () => pmAdminApi.propertyRequests.list(filter || undefined).then(setList).catch(() => setList([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (r) => {
    if (!window.confirm(`Approve "${r.property_name}"? A live property will be created under ${r.company_name}.`)) return;
    await pmAdminApi.propertyRequests.decide(r.id, {
      decision: 'approve',
      auto_approval_limit_override: decision.limit ? Number(decision.limit) : null,
    });
    setOpen(null); load();
  };

  const reject = async (r) => {
    if (!decision.reason.trim()) { alert('Please enter a rejection reason'); return; }
    await pmAdminApi.propertyRequests.decide(r.id, { decision: 'reject', rejection_reason: decision.reason });
    setOpen(null); setDecision({ reason: '', limit: '500' }); load();
  };

  return (
    <div data-testid="admin-property-requests" className="p-6 max-w-6xl space-y-6">
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MGMT</p>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white">Property Requests</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Approve or reject new property submissions from PM portal users. Approval creates the live property under their company and grants access.</p>
      </div>

      <div className="flex gap-2">
        {['Pending', 'Approved', 'Rejected', ''].map((s) => (
          <button key={s || 'all'} data-testid={`pr-filter-${s.toLowerCase() || 'all'}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded text-xs font-medium ${filter === s ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'bg-[#262626] text-[#A3A3A3] hover:text-white'}`}>{s || 'All'}</button>
        ))}
      </div>

      <div className="space-y-3">
        {list.map((r) => {
          const s = STATUS_STYLE[r.status] || STATUS_STYLE.Pending;
          const Icon = s.icon;
          const expanded = open === r.id;
          return (
            <div key={r.id} data-testid={`pr-row-${r.id}`} className="bg-[#141414] border border-[#262626] rounded-md">
              <div className="p-4 flex flex-wrap items-start justify-between gap-3 cursor-pointer" onClick={() => setOpen(expanded ? null : r.id)}>
                <div className="min-w-0">
                  <p className="text-white font-medium">{r.property_name}</p>
                  <p className="text-[#A3A3A3] text-xs">{r.company_name} · {r.address}{r.city ? `, ${r.city}` : ''}{r.state ? `, ${r.state}` : ''}{r.zip ? ` ${r.zip}` : ''} · {r.unit_count || 0} units</p>
                  <p className="text-[#525252] text-xs mt-1">From {r.submitted_by_name} ({r.submitted_by_role}) · {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded inline-flex items-center gap-1 ${s.cls}`}><Icon className="w-3.5 h-3.5" /> {r.status}</span>
              </div>
              {expanded && (
                <div className="border-t border-[#262626] p-4 space-y-3">
                  <div className="grid sm:grid-cols-3 gap-3 text-sm">
                    {r.main_contact_name && <div><p className="text-[#525252] text-xs uppercase tracking-wider">Main</p><p className="text-white">{r.main_contact_name}</p><p className="text-[#A3A3A3] text-xs">{r.main_contact_phone} {r.main_contact_email}</p></div>}
                    {r.maintenance_contact_name && <div><p className="text-[#525252] text-xs uppercase tracking-wider">Maintenance</p><p className="text-white">{r.maintenance_contact_name}</p><p className="text-[#A3A3A3] text-xs">{r.maintenance_contact_phone}</p></div>}
                    {r.emergency_contact_name && <div><p className="text-[#525252] text-xs uppercase tracking-wider">Emergency</p><p className="text-white">{r.emergency_contact_name}</p><p className="text-[#A3A3A3] text-xs">{r.emergency_contact_phone}</p></div>}
                  </div>
                  {r.notes && <p className="text-[#A3A3A3] text-sm border-l-2 border-[#262626] pl-3">{r.notes}</p>}
                  {r.status === 'Rejected' && r.rejection_reason && <p className="text-[#FCA5A5] text-sm">Reason: {r.rejection_reason}</p>}

                  {r.status === 'Pending' && (
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3 space-y-2">
                      <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em]">DECISION</p>
                      <label className="text-[#A3A3A3] text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#D4AF37]" /> Property-override approval limit (blank = use company default $500)</label>
                      <input data-testid={`pr-limit-${r.id}`} type="number" min="0" value={decision.limit} onChange={(e) => setDecision({ ...decision, limit: e.target.value })} className="w-full bg-[#141414] border border-[#262626] rounded h-9 px-3 text-white text-sm" placeholder="500" />
                      <input data-testid={`pr-reject-reason-${r.id}`} placeholder="Rejection reason (only required if rejecting)" value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })} className="w-full bg-[#141414] border border-[#262626] rounded h-9 px-3 text-white text-sm" />
                      <div className="flex gap-2">
                        <button data-testid={`pr-approve-${r.id}`} onClick={() => approve(r)} className="flex-1 bg-[#10B981] text-white h-9 rounded text-sm font-medium hover:bg-[#34D399]">Approve & Create Property</button>
                        <button data-testid={`pr-reject-${r.id}`} onClick={() => reject(r)} className="flex-1 bg-[#7F1D1D] text-white h-9 rounded text-sm font-medium hover:bg-[#EF4444]">Reject</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && <p className="text-[#525252] text-sm">No requests {filter ? `in "${filter}" status` : 'yet'}.</p>}
      </div>
    </div>
  );
};

export default PmPropertyRequests;
